import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthShell } from "@/components/layout/AuthShell";
import { Field } from "@/components/shared/Common";
import { api, errMsg } from "@/lib/api";

const STEPS = ["Verificación", "Empresa", "Administrador", "Confirmar"];
const EMPRESA = ["razon_social", "rut", "giro", "direccion", "telefono", "email"];
const EMPRESA_LABELS = { razon_social: "Razón social *", rut: "RUT *", giro: "Giro", direccion: "Dirección", telefono: "Teléfono", email: "Email" };

const Check = ({ ok, label }) => (
  <div className="flex items-center gap-2 text-sm">{ok ? <CheckCircle2 size={16} className="text-[#258F00]" /> : <XCircle size={16} className="text-[#d4a000]" />}{label}</div>
);

export default function Install({ onInstalled }) {
  const [step, setStep] = useState(0);
  const [token, setToken] = useState("");
  const [checks, setChecks] = useState(null);
  const [empresa, setEmpresa] = useState(Object.fromEntries(EMPRESA.map((k) => [k, ""])));
  const [admin, setAdmin] = useState({ name: "", email: "", username: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const runCheck = async () => {
    setError("");
    try { const { data } = await api.post("/install/check", { token }); setChecks(data); setStep(1); }
    catch (e) { setError(errMsg(e)); }
  };

  const next = () => {
    setError("");
    if (step === 1 && (!empresa.razon_social || !empresa.rut)) return setError("Razón social y RUT son obligatorios");
    if (step === 2 && admin.password !== admin.confirm) return setError("Las contraseñas no coinciden");
    if (step === 2 && (!admin.name || !admin.email || !admin.username)) return setError("Complete todos los datos del administrador");
    setStep(step + 1);
  };

  const install = async () => {
    setError("");
    try {
      const { confirm, ...adminData } = admin;
      await api.post("/install", { token, empresa, admin: adminData });
      onInstalled();
      navigate("/login", { replace: true });
    } catch (e) { setError(errMsg(e)); }
  };

  return (
    <AuthShell title="Asistente de instalación" subtitle="Configuración inicial de SYC CONTROL. Solo se ejecuta una vez.">
      <div className="flex gap-1.5 mb-8" data-testid="install-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#258F00]" : "bg-[#e4e8e1]"}`} />
            <div className={`mt-1.5 text-[11px] font-semibold ${i === step ? "text-[#258F00]" : "text-[#8a9584]"}`}>{s}</div>
          </div>
        ))}
      </div>
      <div className="space-y-4">
        {step === 0 && (
          <Field label="Token de instalación" hint="Definido por el responsable técnico en la variable INSTALL_TOKEN del servidor.">
            <Input data-testid="install-token-input" value={token} onChange={(e) => setToken(e.target.value)} className="h-11" />
          </Field>
        )}
        {step === 1 && (
          <>
            {checks && (
              <div className="rounded-xl border border-[#e4e8e1] bg-white p-4 space-y-1.5" data-testid="install-checks">
                <Check ok={checks.database} label="Base de datos conectada" />
                <Check ok label={`Migraciones aplicadas: ${checks.migrations.join(", ")}`} />
                <Check ok label={`${checks.roles} roles base · ${checks.permissions} privilegios`} />
                <Check ok={checks.smtp_configured} label={checks.smtp_configured ? "SMTP configurado" : `SMTP pendiente (correos de ${checks.mail_from} quedarán simulados)`} />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {EMPRESA.map((k) => (
                <Field key={k} label={EMPRESA_LABELS[k]}>
                  <Input data-testid={`install-empresa-${k}-input`} value={empresa[k]} onChange={(e) => setEmpresa({ ...empresa, [k]: e.target.value })} />
                </Field>
              ))}
            </div>
          </>
        )}
        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre completo"><Input data-testid="install-admin-name-input" value={admin.name} onChange={(e) => setAdmin({ ...admin, name: e.target.value })} /></Field>
            <Field label="Usuario"><Input data-testid="install-admin-username-input" value={admin.username} onChange={(e) => setAdmin({ ...admin, username: e.target.value })} /></Field>
            <div className="sm:col-span-2"><Field label="Email"><Input data-testid="install-admin-email-input" type="email" value={admin.email} onChange={(e) => setAdmin({ ...admin, email: e.target.value })} /></Field></div>
            <Field label="Contraseña" hint="Mín. 10 caracteres, letras y números"><Input data-testid="install-admin-password-input" type="password" value={admin.password} onChange={(e) => setAdmin({ ...admin, password: e.target.value })} /></Field>
            <Field label="Confirmar"><Input data-testid="install-admin-confirm-input" type="password" value={admin.confirm} onChange={(e) => setAdmin({ ...admin, confirm: e.target.value })} /></Field>
          </div>
        )}
        {step === 3 && (
          <div className="rounded-xl border border-[#e4e8e1] bg-white p-5 text-sm space-y-2" data-testid="install-summary">
            <div><b>Empresa:</b> {empresa.razon_social} · {empresa.rut}</div>
            <div><b>Administrador:</b> {admin.name} ({admin.email}) · usuario <span className="font-mono">{admin.username}</span></div>
            <div className="text-[#5b6656]">Se asignará el rol Administrador Sistema con alcance total. Al finalizar, el asistente quedará deshabilitado.</div>
          </div>
        )}
        {error && <div data-testid="install-error" className="rounded-lg border border-[#f5c2c0] bg-[#fdeaea] px-3 py-2 text-sm text-[#b42318]">{error}</div>}
        <div className="flex gap-3 pt-2">
          {step > 0 && <Button data-testid="install-back-btn" variant="outline" className="rounded-full" onClick={() => setStep(step - 1)}>Atrás</Button>}
          {step === 0 && <Button data-testid="install-check-btn" className="flex-1 rounded-full h-11" onClick={runCheck}>Verificar</Button>}
          {step > 0 && step < 3 && <Button data-testid="install-next-btn" className="flex-1 rounded-full h-11" onClick={next}>Continuar</Button>}
          {step === 3 && <Button data-testid="install-submit-btn" className="flex-1 rounded-full h-11" onClick={install}>Instalar SYC CONTROL</Button>}
        </div>
      </div>
    </AuthShell>
  );
}
